'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function MatrixCalculator() {
  const t = useTranslations("tools.matrixCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Matrix state
  const [matrixA, setMatrixA] = useState<number[][]>([[1, 0, 0], [0, 1, 0], [0, 0, 1]]);
  const [matrixB, setMatrixB] = useState<number[][]>([[1, 0, 0], [0, 1, 0], [0, 0, 1]]);
  const [rowsA, setRowsA] = useState(3);
  const [colsA, setColsA] = useState(3);
  const [rowsB, setRowsB] = useState(3);
  const [colsB, setColsB] = useState(3);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '2rem' },
      { padding: '3rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '3rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        borderRadius: '1rem',
        marginBottom: '2rem',
        boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.1)'
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { maxWidth: '100%', margin: '0 auto' },
      {},
      { maxWidth: '900px' },
      { maxWidth: '1100px' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    calculatorGrid: getResponsiveStyle(
      { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr 1fr' },
      { gridTemplateColumns: '1fr 1fr' }
    ),
    matrixPanel: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      },
      { padding: '1rem' },
      { padding: '1.25rem' },
      { padding: '1.5rem' }
    ),
    matrixPanelH3: {
      color: '#2563eb',
      marginBottom: '1rem',
      fontSize: '1.25rem',
      fontWeight: 600
    },
    dimensionControls: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      marginBottom: '1rem',
      flexWrap: 'wrap' as const
    },
    dimensionGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    dimensionLabel: {
      fontWeight: 600,
      color: '#475569',
      fontSize: '0.9rem'
    },
    dimensionInput: getResponsiveStyle(
      {
        padding: '0.5rem',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        textAlign: 'center' as const
      },
      { width: '50px' },
      { width: '60px' },
      { width: '60px' }
    ),
    dimensionBtns: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '2px'
    },
    dimBtn: {
      width: '24px',
      height: '20px',
      border: 'none',
      background: '#2563eb',
      color: 'white',
      cursor: 'pointer',
      fontSize: '0.75rem',
      borderRadius: '3px',
      transition: 'background 0.2s'
    },
    matrixInputGrid: {
      display: 'inline-block',
      borderLeft: '2px solid #1e293b',
      borderRight: '2px solid #1e293b',
      padding: '0.5rem 0.25rem',
      margin: '1rem 0',
      overflowX: 'auto' as const
    },
    matrixRow: {
      display: 'flex',
      gap: '0.25rem'
    },
    matrixCell: getResponsiveStyle(
      {
        padding: '0.5rem',
        border: '1px solid #e2e8f0',
        borderRadius: '0.25rem',
        textAlign: 'center' as const,
        fontSize: '0.9rem'
      },
      { width: '50px' },
      { width: '60px' },
      { width: '60px' }
    ),
    actionButtons: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: '0.5rem',
      marginTop: '1rem'
    },
    actionBtn: getResponsiveStyle(
      {
        border: 'none',
        background: '#64748b',
        color: 'white',
        borderRadius: '0.5rem',
        cursor: 'pointer',
        transition: 'all 0.3s',
        fontWeight: 500
      },
      { padding: '0.5rem 0.75rem', fontSize: '0.85rem' },
      { padding: '0.5rem 1rem', fontSize: '0.9rem' },
      { padding: '0.5rem 1rem', fontSize: '0.9rem' }
    ),
    operationsPanel: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        marginBottom: '2rem'
      },
      { padding: '1rem' },
      { padding: '1.25rem' },
      { padding: '1.5rem' }
    ),
    operationButtons: getResponsiveStyle(
      { display: 'grid', gap: '1rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: 'repeat(3, 1fr)' },
      { gridTemplateColumns: 'repeat(3, 1fr)' }
    ),
    opBtn: getResponsiveStyle(
      {
        border: '2px solid #e2e8f0',
        background: 'white',
        color: '#1e293b',
        borderRadius: '0.75rem',
        cursor: 'pointer',
        fontWeight: 600,
        transition: 'all 0.3s'
      },
      { padding: '0.75rem', fontSize: '0.9rem' },
      { padding: '1rem', fontSize: '1rem' },
      { padding: '1rem', fontSize: '1rem' }
    ),
    results: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    resultsH3: {
      color: '#059669',
      marginBottom: '1.5rem',
      fontSize: '1.5rem',
      fontWeight: 600
    },
    resultMatrix: {
      display: 'inline-block',
      borderLeft: '3px solid #1e293b',
      borderRight: '3px solid #1e293b',
      padding: '1rem 0.5rem',
      margin: '1rem 0',
      overflowX: 'auto' as const
    },
    resultRow: {
      display: 'flex',
      gap: '1rem',
      margin: '0.25rem 0'
    },
    resultCell: {
      minWidth: '80px',
      textAlign: 'right' as const,
      fontFamily: "'Courier New', monospace",
      fontSize: '1rem',
      color: '#1e293b'
    },
    errorMessage: {
      background: '#fee2e2',
      color: '#991b1b',
      padding: '1rem',
      borderRadius: '0.5rem',
      marginTop: '1rem'
    },
    resultLabel: {
      fontSize: '1.25rem',
      color: '#475569',
      marginBottom: '1rem',
      display: 'block',
      fontWeight: 500
    },
    scalarResult: {
      fontSize: '2rem',
      color: '#2563eb',
      fontWeight: 700
    },
    glassCard: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(255, 200, 180, 0.2)',
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    list: getResponsiveStyle(
      { marginLeft: '1.5rem', lineHeight: 1.8, color: colors.textSecondary },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    listItem: {
      marginBottom: '0.75rem'
    },
    faqItem: {
      marginBottom: '1.5rem'
    },
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.25rem' }
    ),
    faqAnswer: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    reference: {
      marginBottom: '0.75rem'
    },
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      fontSize: '1rem',
      transition: 'color 0.3s'
    }
  };

  // Matrix operations
  const adjustDim = (matrix: 'a' | 'b', dim: 'rows' | 'cols', delta: number) => {
    if (matrix === 'a') {
      if (dim === 'rows') {
        const newRows = Math.max(1, Math.min(10, rowsA + delta));
        setRowsA(newRows);
        resizeMatrix('a', newRows, colsA);
      } else {
        const newCols = Math.max(1, Math.min(10, colsA + delta));
        setColsA(newCols);
        resizeMatrix('a', rowsA, newCols);
      }
    } else {
      if (dim === 'rows') {
        const newRows = Math.max(1, Math.min(10, rowsB + delta));
        setRowsB(newRows);
        resizeMatrix('b', newRows, colsB);
      } else {
        const newCols = Math.max(1, Math.min(10, colsB + delta));
        setColsB(newCols);
        resizeMatrix('b', rowsB, newCols);
      }
    }
  };

  const resizeMatrix = (matrix: 'a' | 'b', newRows: number, newCols: number) => {
    const current = matrix === 'a' ? matrixA : matrixB;
    const newMatrix: number[][] = [];

    for (let i = 0; i < newRows; i++) {
      newMatrix[i] = [];
      for (let j = 0; j < newCols; j++) {
        newMatrix[i][j] = current[i] && current[i][j] !== undefined ? current[i][j] : 0;
      }
    }

    if (matrix === 'a') {
      setMatrixA(newMatrix);
    } else {
      setMatrixB(newMatrix);
    }
  };

  const updateCell = (matrix: 'a' | 'b', row: number, col: number, value: string) => {
    const numValue = value === '' ? 0 : parseFloat(value);
    if (matrix === 'a') {
      const newMatrix = [...matrixA];
      newMatrix[row][col] = numValue;
      setMatrixA(newMatrix);
    } else {
      const newMatrix = [...matrixB];
      newMatrix[row][col] = numValue;
      setMatrixB(newMatrix);
    }
  };

  const fillMatrix = (matrix: 'a' | 'b', type: 'zero' | 'one' | 'identity' | 'random') => {
    const rows = matrix === 'a' ? rowsA : rowsB;
    const cols = matrix === 'a' ? colsA : colsB;
    const newMatrix: number[][] = [];

    for (let i = 0; i < rows; i++) {
      newMatrix[i] = [];
      for (let j = 0; j < cols; j++) {
        if (type === 'random') {
          newMatrix[i][j] = Math.floor(Math.random() * 10);
        } else if (type === 'identity') {
          newMatrix[i][j] = i === j ? 1 : 0;
        } else if (type === 'one') {
          newMatrix[i][j] = 1;
        } else {
          newMatrix[i][j] = 0;
        }
      }
    }

    if (matrix === 'a') {
      setMatrixA(newMatrix);
    } else {
      setMatrixB(newMatrix);
    }
  };

  const clearMatrix = (matrix: 'a' | 'b') => {
    fillMatrix(matrix, 'zero');
  };

  const formatNumber = (num: number): string => {
    if (Math.abs(num) < 0.0001 && num !== 0) return num.toExponential(4);
    return (Math.round(num * 10000) / 10000).toString();
  };

  const add = (a: number[][], b: number[][]): number[][] => {
    if (a.length !== b.length || a[0].length !== b[0].length) {
      throw new Error(t('errorSameDimensions'));
    }
    const result: number[][] = [];
    for (let i = 0; i < a.length; i++) {
      result[i] = [];
      for (let j = 0; j < a[0].length; j++) {
        result[i][j] = a[i][j] + b[i][j];
      }
    }
    return result;
  };

  const subtract = (a: number[][], b: number[][]): number[][] => {
    if (a.length !== b.length || a[0].length !== b[0].length) {
      throw new Error(t('errorSameDimensions'));
    }
    const result: number[][] = [];
    for (let i = 0; i < a.length; i++) {
      result[i] = [];
      for (let j = 0; j < a[0].length; j++) {
        result[i][j] = a[i][j] - b[i][j];
      }
    }
    return result;
  };

  const multiply = (a: number[][], b: number[][]): number[][] => {
    if (a[0].length !== b.length) {
      throw new Error(t('errorMultiplyDimensions'));
    }
    const result: number[][] = [];
    for (let i = 0; i < a.length; i++) {
      result[i] = [];
      for (let j = 0; j < b[0].length; j++) {
        result[i][j] = 0;
        for (let k = 0; k < a[0].length; k++) {
          result[i][j] += a[i][k] * b[k][j];
        }
      }
    }
    return result;
  };

  const transpose = (matrix: number[][]): number[][] => {
    const result: number[][] = [];
    for (let i = 0; i < matrix[0].length; i++) {
      result[i] = [];
      for (let j = 0; j < matrix.length; j++) {
        result[i][j] = matrix[j][i];
      }
    }
    return result;
  };

  const getMinor = (matrix: number[][], row: number, col: number): number[][] => {
    const minor: number[][] = [];
    for (let i = 0; i < matrix.length; i++) {
      if (i === row) continue;
      const newRow: number[] = [];
      for (let j = 0; j < matrix[i].length; j++) {
        if (j === col) continue;
        newRow.push(matrix[i][j]);
      }
      minor.push(newRow);
    }
    return minor;
  };

  const determinant = (matrix: number[][]): number => {
    if (matrix.length !== matrix[0].length) {
      throw new Error(t('errorSquareMatrix'));
    }
    const n = matrix.length;

    if (n === 1) return matrix[0][0];
    if (n === 2) return matrix[0][0] * matrix[1][1] - matrix[0][1] * matrix[1][0];

    let det = 0;
    for (let j = 0; j < n; j++) {
      det += Math.pow(-1, j) * matrix[0][j] * determinant(getMinor(matrix, 0, j));
    }
    return det;
  };

  const inverse = (matrix: number[][]): number[][] => {
    if (matrix.length !== matrix[0].length) {
      throw new Error(t('errorSquareMatrix'));
    }

    const det = determinant(matrix);
    if (Math.abs(det) < 1e-10) {
      throw new Error(t('errorSingularMatrix'));
    }

    const n = matrix.length;
    const augmented: number[][] = [];

    for (let i = 0; i < n; i++) {
      augmented[i] = [];
      for (let j = 0; j < n; j++) {
        augmented[i][j] = matrix[i][j];
      }
      for (let j = 0; j < n; j++) {
        augmented[i][n + j] = i === j ? 1 : 0;
      }
    }

    for (let i = 0; i < n; i++) {
      let maxRow = i;
      for (let k = i + 1; k < n; k++) {
        if (Math.abs(augmented[k][i]) > Math.abs(augmented[maxRow][i])) {
          maxRow = k;
        }
      }

      [augmented[i], augmented[maxRow]] = [augmented[maxRow], augmented[i]];

      const pivot = augmented[i][i];
      for (let j = 0; j < 2 * n; j++) {
        augmented[i][j] /= pivot;
      }

      for (let k = 0; k < n; k++) {
        if (k !== i) {
          const factor = augmented[k][i];
          for (let j = 0; j < 2 * n; j++) {
            augmented[k][j] -= factor * augmented[i][j];
          }
        }
      }
    }

    const result: number[][] = [];
    for (let i = 0; i < n; i++) {
      result[i] = [];
      for (let j = 0; j < n; j++) {
        result[i][j] = augmented[i][n + j];
      }
    }
    return result;
  };

  const calculate = (operation: string) => {
    setError('');
    setResult(null);

    try {
      let calcResult: any;

      switch(operation) {
        case 'add':
          calcResult = { type: 'matrix', label: t('resultAdd'), data: add(matrixA, matrixB) };
          break;
        case 'subtract':
          calcResult = { type: 'matrix', label: t('resultSubtract'), data: subtract(matrixA, matrixB) };
          break;
        case 'multiply':
          calcResult = { type: 'matrix', label: t('resultMultiply'), data: multiply(matrixA, matrixB) };
          break;
        case 'transpose-a':
          calcResult = { type: 'matrix', label: t('resultTransposeA'), data: transpose(matrixA) };
          break;
        case 'transpose-b':
          calcResult = { type: 'matrix', label: t('resultTransposeB'), data: transpose(matrixB) };
          break;
        case 'determinant-a':
          calcResult = { type: 'scalar', label: t('resultDeterminantA'), data: determinant(matrixA) };
          break;
        case 'determinant-b':
          calcResult = { type: 'scalar', label: t('resultDeterminantB'), data: determinant(matrixB) };
          break;
        case 'inverse-a':
          calcResult = { type: 'matrix', label: t('resultInverseA'), data: inverse(matrixA) };
          break;
        case 'inverse-b':
          calcResult = { type: 'matrix', label: t('resultInverseB'), data: inverse(matrixB) };
          break;
      }

      setResult(calcResult);
    } catch (e: any) {
      setError(e.message);
    }
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.summaryText}>{t('subtitle')}</p>
      </header>

      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <h2 style={styles.h2}>{t('toolTitle')}</h2>
          </div>

          <div style={styles.calculatorGrid}>
            <div style={styles.matrixPanel}>
              <h3 style={styles.matrixPanelH3}>{t('matrixA')}</h3>
              <div style={styles.dimensionControls}>
                <div style={styles.dimensionGroup}>
                  <label style={styles.dimensionLabel}>{t('rows')}:</label>
                  <input
                    type="number"
                    style={styles.dimensionInput}
                    value={rowsA}
                    min="1"
                    max="10"
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 1;
                      setRowsA(Math.max(1, Math.min(10, val)));
                      resizeMatrix('a', Math.max(1, Math.min(10, val)), colsA);
                    }}
                  />
                  <div style={styles.dimensionBtns}>
                    <button
                      style={styles.dimBtn}
                      onClick={() => adjustDim('a', 'rows', 1)}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#1d4ed8'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#2563eb'}
                    >▲</button>
                    <button
                      style={styles.dimBtn}
                      onClick={() => adjustDim('a', 'rows', -1)}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#1d4ed8'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#2563eb'}
                    >▼</button>
                  </div>
                </div>
                <div style={styles.dimensionGroup}>
                  <label style={styles.dimensionLabel}>{t('cols')}:</label>
                  <input
                    type="number"
                    style={styles.dimensionInput}
                    value={colsA}
                    min="1"
                    max="10"
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 1;
                      setColsA(Math.max(1, Math.min(10, val)));
                      resizeMatrix('a', rowsA, Math.max(1, Math.min(10, val)));
                    }}
                  />
                  <div style={styles.dimensionBtns}>
                    <button
                      style={styles.dimBtn}
                      onClick={() => adjustDim('a', 'cols', 1)}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#1d4ed8'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#2563eb'}
                    >▲</button>
                    <button
                      style={styles.dimBtn}
                      onClick={() => adjustDim('a', 'cols', -1)}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#1d4ed8'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#2563eb'}
                    >▼</button>
                  </div>
                </div>
              </div>
              <div style={styles.matrixInputGrid}>
                {matrixA.map((row, i) => (
                  <div key={i} style={styles.matrixRow}>
                    {row.map((cell, j) => (
                      <input
                        key={`${i}-${j}`}
                        type="number"
                        style={{
                          ...styles.matrixCell,
                          outline: 'none'
                        }}
                        value={cell}
                        step="any"
                        onChange={(e) => updateCell('a', i, j, e.target.value)}
                        onFocus={(e) => {
                          e.currentTarget.style.outline = '2px solid #2563eb';
                          e.currentTarget.style.borderColor = '#2563eb';
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.outline = 'none';
                          e.currentTarget.style.borderColor = '#e2e8f0';
                        }}
                      />
                    ))}
                  </div>
                ))}
              </div>
              <div style={styles.actionButtons}>
                <button
                  style={styles.actionBtn}
                  onClick={() => fillMatrix('a', 'zero')}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#475569'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#64748b'}
                >{t('fillZero')}</button>
                <button
                  style={styles.actionBtn}
                  onClick={() => fillMatrix('a', 'one')}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#475569'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#64748b'}
                >{t('fillOne')}</button>
                <button
                  style={styles.actionBtn}
                  onClick={() => fillMatrix('a', 'identity')}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#475569'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#64748b'}
                >{t('fillIdentity')}</button>
                <button
                  style={styles.actionBtn}
                  onClick={() => fillMatrix('a', 'random')}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#475569'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#64748b'}
                >{t('fillRandom')}</button>
                <button
                  style={styles.actionBtn}
                  onClick={() => clearMatrix('a')}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#475569'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#64748b'}
                >{t('clear')}</button>
              </div>
            </div>

            <div style={styles.matrixPanel}>
              <h3 style={styles.matrixPanelH3}>{t('matrixB')}</h3>
              <div style={styles.dimensionControls}>
                <div style={styles.dimensionGroup}>
                  <label style={styles.dimensionLabel}>{t('rows')}:</label>
                  <input
                    type="number"
                    style={styles.dimensionInput}
                    value={rowsB}
                    min="1"
                    max="10"
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 1;
                      setRowsB(Math.max(1, Math.min(10, val)));
                      resizeMatrix('b', Math.max(1, Math.min(10, val)), colsB);
                    }}
                  />
                  <div style={styles.dimensionBtns}>
                    <button
                      style={styles.dimBtn}
                      onClick={() => adjustDim('b', 'rows', 1)}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#1d4ed8'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#2563eb'}
                    >▲</button>
                    <button
                      style={styles.dimBtn}
                      onClick={() => adjustDim('b', 'rows', -1)}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#1d4ed8'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#2563eb'}
                    >▼</button>
                  </div>
                </div>
                <div style={styles.dimensionGroup}>
                  <label style={styles.dimensionLabel}>{t('cols')}:</label>
                  <input
                    type="number"
                    style={styles.dimensionInput}
                    value={colsB}
                    min="1"
                    max="10"
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 1;
                      setColsB(Math.max(1, Math.min(10, val)));
                      resizeMatrix('b', rowsB, Math.max(1, Math.min(10, val)));
                    }}
                  />
                  <div style={styles.dimensionBtns}>
                    <button
                      style={styles.dimBtn}
                      onClick={() => adjustDim('b', 'cols', 1)}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#1d4ed8'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#2563eb'}
                    >▲</button>
                    <button
                      style={styles.dimBtn}
                      onClick={() => adjustDim('b', 'cols', -1)}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#1d4ed8'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#2563eb'}
                    >▼</button>
                  </div>
                </div>
              </div>
              <div style={styles.matrixInputGrid}>
                {matrixB.map((row, i) => (
                  <div key={i} style={styles.matrixRow}>
                    {row.map((cell, j) => (
                      <input
                        key={`${i}-${j}`}
                        type="number"
                        style={{
                          ...styles.matrixCell,
                          outline: 'none'
                        }}
                        value={cell}
                        step="any"
                        onChange={(e) => updateCell('b', i, j, e.target.value)}
                        onFocus={(e) => {
                          e.currentTarget.style.outline = '2px solid #2563eb';
                          e.currentTarget.style.borderColor = '#2563eb';
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.outline = 'none';
                          e.currentTarget.style.borderColor = '#e2e8f0';
                        }}
                      />
                    ))}
                  </div>
                ))}
              </div>
              <div style={styles.actionButtons}>
                <button
                  style={styles.actionBtn}
                  onClick={() => fillMatrix('b', 'zero')}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#475569'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#64748b'}
                >{t('fillZero')}</button>
                <button
                  style={styles.actionBtn}
                  onClick={() => fillMatrix('b', 'one')}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#475569'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#64748b'}
                >{t('fillOne')}</button>
                <button
                  style={styles.actionBtn}
                  onClick={() => fillMatrix('b', 'identity')}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#475569'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#64748b'}
                >{t('fillIdentity')}</button>
                <button
                  style={styles.actionBtn}
                  onClick={() => fillMatrix('b', 'random')}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#475569'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#64748b'}
                >{t('fillRandom')}</button>
                <button
                  style={styles.actionBtn}
                  onClick={() => clearMatrix('b')}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#475569'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#64748b'}
                >{t('clear')}</button>
              </div>
            </div>
          </div>

          <div style={styles.operationsPanel}>
            <h3 style={{ ...styles.h2, marginBottom: '1rem' }}>{t('operations')}</h3>
            <div style={styles.operationButtons}>
              <button
                style={styles.opBtn}
                onClick={() => calculate('add')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#2563eb';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.borderColor = '#2563eb';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.color = '#1e293b';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >{t('opAdd')}</button>
              <button
                style={styles.opBtn}
                onClick={() => calculate('subtract')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#2563eb';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.borderColor = '#2563eb';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.color = '#1e293b';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >{t('opSubtract')}</button>
              <button
                style={styles.opBtn}
                onClick={() => calculate('multiply')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#2563eb';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.borderColor = '#2563eb';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.color = '#1e293b';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >{t('opMultiply')}</button>
              <button
                style={styles.opBtn}
                onClick={() => calculate('transpose-a')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#2563eb';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.borderColor = '#2563eb';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.color = '#1e293b';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >{t('opTransposeA')}</button>
              <button
                style={styles.opBtn}
                onClick={() => calculate('transpose-b')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#2563eb';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.borderColor = '#2563eb';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.color = '#1e293b';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >{t('opTransposeB')}</button>
              <button
                style={styles.opBtn}
                onClick={() => calculate('determinant-a')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#2563eb';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.borderColor = '#2563eb';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.color = '#1e293b';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >{t('opDetA')}</button>
              <button
                style={styles.opBtn}
                onClick={() => calculate('determinant-b')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#2563eb';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.borderColor = '#2563eb';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.color = '#1e293b';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >{t('opDetB')}</button>
              <button
                style={styles.opBtn}
                onClick={() => calculate('inverse-a')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#2563eb';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.borderColor = '#2563eb';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.color = '#1e293b';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >{t('opInverseA')}</button>
              <button
                style={styles.opBtn}
                onClick={() => calculate('inverse-b')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#2563eb';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.borderColor = '#2563eb';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.color = '#1e293b';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >{t('opInverseB')}</button>
            </div>
          </div>

          {(result || error) && (
            <div style={styles.results}>
              <h3 style={styles.resultsH3}>{t('resultTitle')}</h3>
              {error && (
                <div style={styles.errorMessage}>{error}</div>
              )}
              {result && result.type === 'matrix' && (
                <>
                  <div style={styles.resultLabel}>{result.label}</div>
                  <div style={styles.resultMatrix}>
                    {result.data.map((row: number[], i: number) => (
                      <div key={i} style={styles.resultRow}>
                        {row.map((cell: number, j: number) => (
                          <div key={j} style={styles.resultCell}>
                            {formatNumber(cell)}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </>
              )}
              {result && result.type === 'scalar' && (
                <>
                  <div style={styles.resultLabel}>{result.label}</div>
                  <div style={styles.scalarResult}>{formatNumber(result.data)}</div>
                </>
              )}
            </div>
          )}
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introPara1')}</p>
        <p style={styles.paragraph}>{t('introPara2')}</p>
        <p style={styles.paragraph}>{t('introPara3')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t('howToStep1')}</li>
          <li style={styles.listItem}>{t('howToStep2')}</li>
          <li style={styles.listItem}>{t('howToStep3')}</li>
          <li style={styles.listItem}>{t('howToStep4')}</li>
          <li style={styles.listItem}>{t('howToStep5')}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('insightsTitle')}</h2>
        <p style={styles.paragraph}>{t('insightsPara1')}</p>
        <p style={styles.paragraph}>{t('insightsPara2')}</p>
        <p style={styles.paragraph}>{t('insightsPara3')}</p>
        <p style={styles.paragraph}>{t('insightsPara4')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('detailsTitle')}</h2>
        <h3 style={{ ...styles.h2, fontSize: '1.5rem', marginTop: '1.5rem' }}>{t('detailsOpsTitle')}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}><strong>{t('detailsAddSub')}</strong> {t('detailsAddSubDesc')}</li>
          <li style={styles.listItem}><strong>{t('detailsMult')}</strong> {t('detailsMultDesc')}</li>
          <li style={styles.listItem}><strong>{t('detailsTranspose')}</strong> {t('detailsTransposeDesc')}</li>
          <li style={styles.listItem}><strong>{t('detailsDet')}</strong> {t('detailsDetDesc')}</li>
          <li style={styles.listItem}><strong>{t('detailsInverse')}</strong> {t('detailsInverseDesc')}</li>
        </ul>

        <h3 style={{ ...styles.h2, fontSize: '1.5rem', marginTop: '1.5rem' }}>{t('detailsBestTitle')}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('detailsBest1')}</li>
          <li style={styles.listItem}>{t('detailsBest2')}</li>
          <li style={styles.listItem}>{t('detailsBest3')}</li>
          <li style={styles.listItem}>{t('detailsBest4')}</li>
          <li style={styles.listItem}>{t('detailsBest5')}</li>
        </ul>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq1Q')}</h3>
          <p style={styles.faqAnswer}>{t('faq1A')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq2Q')}</h3>
          <p style={styles.faqAnswer}>{t('faq2A')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq3Q')}</h3>
          <p style={styles.faqAnswer}>{t('faq3A')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq4Q')}</h3>
          <p style={styles.faqAnswer}>{t('faq4A')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq5Q')}</h3>
          <p style={styles.faqAnswer}>{t('faq5A')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq6Q')}</h3>
          <p style={styles.faqAnswer}>{t('faq6A')}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>
        <div style={styles.reference}>
          <a
            href="https://www.meegle.com/en_us/topics/decision-matrix/decision-matrix-for-health-and-wellness"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.referenceLink}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
            onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
          >
            {t('reference1')}
          </a>
        </div>
      </section>
    </div>
  );
}